// The landing screen (deployment.md §3; mockup round fifteen A, "Twin
// Consoles"; R229).
//
// It is the first thing a visitor with no session sees, and it asks one
// question: which of the two games do you want? Two instrument consoles
// stand side by side, each framing a live preview of its own mode and
// carrying the three readouts a player chooses on. The account rail is on
// the right on a wide screen and at the foot of the page on a telephone.
//
// The screen resolves one of two things:
//
//   - a **join**, when the visitor pressed a console. A guest account is
//     opened first where there is none, and `main.ts` dials what comes
//     back.
//   - a **signed-in account**, when the visitor signed in instead. There is
//     no reload: `main.ts` shows the hub with the account this screen read.
//
// Each console plays the mode reel of its mode, a recorded video of the
// real engine, through `ui/reels.ts` (R1535). The whole chrome
// follows `ui/overlay.ts`: the monospace stack, uppercase labels, the
// instrument palette of `ui-map-interface.md` §2, the release on every
// control because the screen scrolls (§16.8, R1206), and a 44 px minimum
// target because a telephone is a target platform.
//
// The screen also owns the sound of the menu (`audio.md` §3.6, R227). It
// turns menu mode on while it is up and off when it resolves, and it plays
// the menu cues of §4.7 on its own actions. The first press on the page is
// the gesture that unlocks the audio context, and the context resumes
// asynchronously, so the cue of that first press is silent by design.
//
// **The rail draws no sign-in method this deployment does not offer**
// (R621). `GET /api/auth/methods` names them, the screen reads it beside
// the two readout reads, and an APPLE or a GOOGLE button appears only for a
// name in that answer. Nothing is drawn until the answer lands, a failed
// read draws none, and the OR divider goes with the last button, because a
// divider with nothing above it divides nothing. The email form is always
// there: the register route needs no configuration.
//
// **FORGOT PASSWORD? says the same thing to everybody** (R624). It takes
// the address out of the email box, asks the meta server for a reset link,
// and prints one notice whether an account holds that address or not. The
// route answers the same way for the same reason: a screen that tells the
// two apart is a way to find out who plays here.
//
// **`/?forgot=1` opens the screen on that step** (R2088). The reset page
// of a spent link points here: the screen puts the focus in the email box
// and says to type the address and press FORGOT PASSWORD?. `fast/entry.ts`
// reads the word with `readForgot` and takes it off the address bar with
// the sign-in words, so a reload or a shared address shows no hint. A
// session never reaches this screen, so a signed-in player who opens the
// address gets the hub, with the session kept.
//
// **No figure on this screen is invented.** Every readout comes from
// `/api/modes` or `/api/worlds`, and a figure that has not arrived is a
// dash. A mode with no open world renders an OPENS-SOON console with a
// disabled CTA rather than a join that cannot work.

import type { AudioDirector } from '../audio/director';
import {
  ApiError, CODE_EMAIL_TAKEN, CODE_INVALID_LOGIN, CODE_REAUTH_REQUIRED, CODE_UNAVAILABLE, messageOf,
  authGuest, authLogin, authLogout, authRegister, forgotPassword, getMe, getModes,
  getSignInMethods, getWorldList, join,
  type ArcadeLive, type Join, type Me, type ModeList, type SignInLink, type WorldList,
} from '../net/api';
import { startOAuth } from '../platform/signin';
import { CODE_MAINTENANCE, SEASON_DAYS } from '../gen/balance';
import { coverIfMaintenance } from './maintenance';
import { assignWithGesture } from './nav';
import { arcadePlayHref } from '../arcade/drillmarks';
import { PressRoute } from './pressroute';
import { clearAllTokens } from '../net/token';
import { esc } from './esc';
import { Reels, reelHtml } from './reels';
import { formatSpan, groupDigits, secondsUntil } from './hub/hubstate';
import { readSection } from './hub/panel';
import { fast as F, hubPlay as MODE, landing as S, PAGE_PREFIX } from '../gen/strings';
import { LOCKUP } from '../gen/presentation';
import { devBrand, signSVGElement } from './sign';
import { closeHowPlays, howModeOf, howPressHtml, openHowPlays } from './howplays';

/**
 * What the screen resolves with.
 *
 * The two arms are the two ways off the screen, and they are deliberately
 * different shapes: a join goes straight into a world, and an account goes
 * into the hub. `main.ts` switches on `kind` and never has to guess.
 */
export type LandingResult =
  | { kind: 'join'; join: Join }
  | { kind: 'signedIn'; me: Me };

/** What the caller knows before the screen opens. */
export interface LandingOptions {
  /** The signed-in account, or null when `/api/me` answered 401. It is
   *  normally null: a session goes to the hub and not to this screen. */
  me: Me | null;
  /** A line to show at once, for example a meta server that is unwell. */
  notice?: string;
  /** A world from a `?world=` deep link. The join prefers it. */
  world?: number;
  /**
   * The hub section and page the page address named at the arrival, for
   * example `?tab=clan&page=cup` (R1649). `fast/entry.ts` read them and
   * took them off the address bar, so a provider button carries them to
   * the start route in place of the address (R1659).
   */
  link?: SignInLink;
  /** The sound director, where this client has one. Absent leaves the
   *  screen silent: every call on it is optional-chained. */
  audio?: AudioDirector;
  /** The address named `?forgot=1` (R2088): the screen opens on the step
   *  of FORGOT PASSWORD?, with the focus in the email box and its hint. */
  forgot?: boolean;
}

/** The page parameter of the reset step of the sign-in screen (R2088). */
export const FORGOT_PARAM = 'forgot';

/** Whether the page address asks for the reset step: `?forgot=1` and no
 *  other value. */
export function readForgot(params: URLSearchParams): boolean {
  return params.get(FORGOT_PARAM) === '1';
}

/**
 * The Aquaphobia sign, without its plate (brand.md §1, R1644). The page is
 * already the deep water the plate stands for. With the name beside it,
 * it is the header lockup (brand.md §2, R1645). The landing has no
 * environment tag, so a page of the dev identity gives it the dev corner
 * at every width (brand.md §15.2).
 */
const MARK = signSVGElement(LOCKUP.headerPx, { plate: false, corner: devBrand() }, 'class="glyph" aria-hidden="true"');

/** The six footer links, in the order of the footer of a static page:
 *  the press kit stands after ABOUT (brand.md §8.3, R1660), and the
 *  history is a section of the about page with no link of its own
 *  (R1736). The last three are the legal ones, and a paid service
 *  operated from Germany must carry them one press from every page, so
 *  they are separate destinations and none of them drops out.
 *
 *  Each link carries a long name and a short one (R732). The stylesheet
 *  shows the long name on a wide screen and the short name on a
 *  telephone, where the row has no room for PRIVACY NOTICE and TERMS OF
 *  SERVICE. Four of the six names are the same in both forms.
 *
 *  The href takes PAGE_PREFIX, so the row leads to the documents in the
 *  language this page picked (R856). */
const FOOT_PAGES = ['about', 'press', 'contact', 'imprint', 'privacy', 'terms'];
export const FOOT_LINKS: [href: string, label: string, short: string, legal: boolean][] =
  FOOT_PAGES.map((page, i) =>
    [`${PAGE_PREFIX}/${page}`, S.foot_links[i], S.foot_links_short[i], i >= FOOT_PAGES.length - 3]);

/**
 * The two sign-in providers, in the order their buttons stand in (R621).
 *
 * The screen draws one of these only when `GET /api/auth/methods` names it,
 * so a deployment with no Apple keys shows no APPLE button. The provider
 * name is the wire word the start route takes; the class and the glyph are
 * the button's look, and the label is the locale's.
 */
const PROVIDERS: [provider: string, cls: string, glyph: string, label: string][] = [
  ['apple', 'ap', '✦', S.oauth_apple],
  ['google', 'go', '◉', S.oauth_google],
];

/** One readout under a console: a label and a value. */
export type Readout = [label: string, value: string];

/** The placeholder for a figure that has not arrived. */
const DASH = '—';

/** A count for a readout, or the dash while the figure has not arrived. */
const count = (n: number | undefined): string =>
  (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? groupDigits(n) : DASH);

/**
 * Whether the ARCADE console offers a join.
 *
 * The rule has two arms, because a client can meet a server that has no
 * arcade fleet behind it:
 *
 *   - The server sent an `arcade` block. Then `live.open` decides, and the
 *     console asks nothing else. The coordinator knows whether it drains
 *     and which categories it serves, and the client must not work that
 *     out again.
 *   - The server sent no block. That is a deployment with no coordinator,
 *     or a server that predates the block. Then the world registry decides,
 *     as it did before: a mode with an open world is open.
 */
export function arcadeIsOpen(live: ArcadeLive | undefined, worlds: number): boolean {
  return live !== undefined ? live.open : worlds > 0;
}

/**
 * The three ARCADE readouts.
 *
 * With an `arcade` block the figures are the coordinator's own: matches
 * fighting now, the human seats in them, and the map categories the fleet
 * serves. Without one the screen falls back on the world registry, which is
 * the only thing an old server or a coordinator-free deployment can say.
 */
export function arcadeReadouts(
  live: ArcadeLive | undefined,
  worlds: number,
  population: number,
): Readout[] {
  if (live !== undefined) {
    return [
      [S.read_matches_running, count(live.running)],
      [S.read_in_the_water, count(live.humans)],
      [S.read_categories_open, count(live.categories)],
    ];
  }
  return [
    [S.read_worlds_open, worlds < 0 ? DASH : String(worlds)],
    [S.read_in_the_water, count(population)],
    [S.read_account, S.read_account_not_needed],
  ];
}

/**
 * Shows the landing screen. It never rejects: every failure renders inline
 * and the screen stays up, because there is nowhere else for the visitor
 * to go.
 */
export function showLanding(opts: LandingOptions): Promise<LandingResult> {
  return new LandingScreen(opts).run();
}

/**
 * The deep link with the section the address names (R1731, §16.16).
 * `fast/entry.ts` reads `tab` and `page` alone, and it leaves `section` on
 * the address bar, so the hub reads it after a password sign-in. A
 * provider button leaves the page, so the link carries the word through
 * the provider as well. The panel of the tab drops a word it does not
 * know.
 */
function withSection(link: SignInLink | undefined): SignInLink | undefined {
  if (link?.tab === undefined) return link;
  const section = readSection(new URLSearchParams(location.search));
  return section === undefined ? link : { ...link, section };
}

class LandingScreen {
  private root = document.createElement('div');
  private me: Me | null;
  private notice: string;
  private world: number;
  private link?: SignInLink;
  private audio?: AudioDirector;

  private error = '';
  private busy = '';
  /** The hint of `?forgot=1` stands until the first action (R2088). */
  private forgotHint: boolean;
  private email = '';
  private password = '';

  private reels = new Reels();

  private modes: ModeList | null = null;
  private worlds: WorldList | null = null;
  /**
   * The sign-in methods this deployment offers, or null while nothing has
   * answered (R621). Null draws no provider button at all, and so does a
   * failed read: a button that cannot work is worse than no button.
   */
  private methods: string[] | null = null;
  /**
   * True once a read of the meta server answered the maintenance code, or
   * answered nothing at all (deployment.md §6.1). The screen then carries
   * the maintenance notice instead of a bare failure, and it probes the
   * gate document: a positive probe puts the same cover over the screen
   * the game page shows, so the page reloads itself when the game is back.
   */
  private gated = false;

  private done!: (r: LandingResult) => void;
  private closed = false;
  /** The press router of the root (R1206). Set by `run`. */
  private press: PressRoute | null = null;

  constructor(opts: LandingOptions) {
    this.me = opts.me;
    this.notice = opts.notice ?? '';
    this.world = opts.world ?? 0;
    this.link = withSection(opts.link);
    this.audio = opts.audio;
    this.forgotHint = opts.forgot ?? false;
    this.root.id = 'landing';
  }

  run(): Promise<LandingResult> {
    document.body.appendChild(this.root);
    // The whole screen is one scroller on a telephone, so every control
    // acts on the release (ui-map-interface.md §16.8, R1206). The router
    // sits on the root once; `render` rewrites the controls under it.
    this.press = new PressRoute(this.root, {
      control: '[data-act]',
      scrolls: () => true,
      act: (el) => this.act(el.dataset.act ?? '', el),
    });
    // Menu mode holds the menu playlist while this screen is up (§3.6).
    this.audio?.menuMusic(true);
    this.render();
    // The reset step puts the focus in the email box; `render` keeps it
    // there through every later rewrite.
    if (this.forgotHint) this.root.querySelector<HTMLInputElement>('input[data-in="email"]')?.focus();
    // Both reads need no session, so they run beside everything else. A
    // failure leaves the readouts as dashes: a figure is a convenience,
    // and it is never the way in.
    getModes().then((m) => { this.modes = m; this.render(); })
      .catch((err) => { this.readGate(err); });
    getWorldList('core').then((w) => { this.worlds = w; this.render(); }).catch(() => { /* dashes */ });
    // Which sign-in methods this deployment offers (R621). It needs no
    // session either, so it runs beside the two readout reads. A failure
    // leaves the rail with the email form alone.
    getSignInMethods()
      .then((m) => { this.methods = m; this.render(); })
      .catch(() => { /* no provider button */ });
    return new Promise<LandingResult>((resolve) => { this.done = resolve; });
  }

  /**
   * Reads one failed call for the maintenance gate (deployment.md §6.1).
   *
   * Two codes say it. `maintenance` is nginx's own refusal shape, written
   * by the gated `/api/` locations while the flag file stands.
   * `api_unavailable` is what the client makes of an answer that was not
   * JSON at all, which is what a proxy in front of a stopped meta server
   * gives. Neither one is proof — a 502 has other causes — so the screen
   * says the line and then ASKS the gate document, and only a positive
   * answer covers the screen.
   */
  private readGate(err: unknown) {
    if (!(err instanceof ApiError)) return;
    if (err.code !== CODE_MAINTENANCE && err.code !== CODE_UNAVAILABLE) return;
    this.gated = true;
    this.render();
    void coverIfMaintenance(err.code === CODE_MAINTENANCE);
  }

  /** Removes the screen. `main.ts` builds what comes next behind it. */
  private close(result: LandingResult) {
    if (this.closed) return;
    this.closed = true;
    this.press?.destroy();
    this.press = null;
    closeHowPlays();
    this.reels.destroy();
    this.root.remove();
    // A join goes into a world, so the music goes back to the player's own
    // playlist. A sign-in stays in the menu, so menu mode stays on and the
    // hub keeps it.
    if (result.kind === 'join') this.audio?.menuMusic(false);
    this.done(result);
  }

  // --- actions ---

  /** Runs one call with the screen in its busy state. */
  private async guard(label: string, fn: () => Promise<void>) {
    if (this.busy !== '') return;
    this.busy = label;
    this.error = '';
    this.notice = '';
    this.forgotHint = false;
    this.render();
    try {
      await fn();
    } catch (err) {
      this.error = messageOf(err);
      this.audio?.play('sfx-menu-error');
    } finally {
      this.busy = '';
      if (!this.closed) this.render();
    }
  }

  /** A console press: a guest account where there is none, then the join. */
  private jumpIn(mode: string) {
    this.audio?.play('sfx-menu-launch');
    void this.guard(S.busy_opening_seat, async () => {
      if (!this.me) {
        // A fresh guest is a fresh identity: any resume token the browser
        // still holds names somebody else's seat, so it goes first.
        clearAllTokens();
        await authGuest();
      }
      // Arcade is a page of its own (arcade.md §10.1). The console still
      // opens the guest account, and then hands the visitor over with
      // `?quick=1`, which is the "straight into the default queue" path of
      // §5.1, or with `?drill=1` while this device holds no drill pass:
      // the first drill comes before the first match (§17.8, R2119). It
      // mints no ticket here: the arcade page runs its own join, and a
      // ticket minted now would expire on the walk.
      if (mode === 'arcade') {
        // With the press's activation attached, so the arcade page may
        // keep the menu music playing without another press (ui/nav.ts).
        assignWithGesture(arcadePlayHref());
        return;
      }
      const j = await join({ mode, world: mode === 'core' ? this.world : 0 });
      this.close({ kind: 'join', join: j });
    });
  }

  /**
   * The one CONTINUE of the rail: it signs in, and it creates the account
   * when there is none.
   *
   * The order matters. A sign-in is tried first, because a returning player
   * is the common case. `invalid_credentials` means either "no such
   * account" or "the wrong password", and the two are the same answer by
   * design: a login route that says which one is a way to find out who has
   * an account here. So the screen tries the registration next, and an
   * `email_taken` from it proves the account exists — the password was
   * wrong, and that is what the visitor is told.
   *
   * A guest session in the cookie makes the registration an upgrade in
   * place, so a guest that signs up keeps its captains and its Honour.
   */
  private submitForm() {
    void this.guard(S.busy_signing_in, async () => {
      try {
        await authLogin(this.email, this.password);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== CODE_INVALID_LOGIN) throw err;
        try {
          await authRegister(this.email, this.password);
        } catch (err2) {
          if (err2 instanceof ApiError && err2.code === CODE_EMAIL_TAKEN) {
            throw new ApiError(401, CODE_INVALID_LOGIN, S.err_password_mismatch);
          }
          throw err2;
        }
      }
      // The session changed: read the account again rather than guess it.
      const me = await getMe();
      // A sign-in that landed on a DIFFERENT account than the session held
      // (a guest, another player on this browser, or nobody) must drop the
      // old identity's resume tokens, or the next dial would offer a seat
      // that is not this account's. The guest-upgrade register keeps the
      // same account id, and then the tokens stay — they are still its own.
      if (me.account.id !== this.me?.account.id) clearAllTokens();
      this.audio?.play('sfx-menu-signin');
      this.close({ kind: 'signedIn', me });
    });
  }

  /**
   * The address the email box holds now.
   *
   * A password manager's fill does not always raise an `input` event, so
   * the box is read back rather than trusted to have reported itself. The
   * FORGOT PASSWORD? control needs the address a hand may never have
   * typed.
   */
  private emailNow(): string {
    const box = this.root.querySelector<HTMLInputElement>('input[data-in="email"]');
    if (box) this.keep(box);
    return this.email.trim();
  }

  /** Stores what one field of the form holds now. */
  private keep(i: HTMLInputElement) {
    if (i.dataset.in === 'email') this.email = i.value;
    else this.password = i.value;
  }

  /**
   * FORGOT PASSWORD? (R624).
   *
   * It takes the address out of the email box, because a visitor who
   * cannot sign in has already typed one. An empty box is answered here
   * and reaches no route: there is nothing to send a link to.
   *
   * The notice says the same thing whether an account holds the address or
   * not, and so does the route: a screen that says "no such account" is a
   * way to find out who plays here.
   */
  private forgot() {
    this.audio?.play('sfx-menu-press');
    this.forgotHint = false;
    const email = this.emailNow();
    if (email === '') {
      this.notice = '';
      this.error = S.err_forgot_no_email;
      this.audio?.play('sfx-menu-error');
      this.render();
      return;
    }
    void this.guard(S.busy_forgot, async () => {
      await forgotPassword(email);
      // A refusal never reaches this line: `guard` catches it and prints
      // the server's own sentence, which is what `mail_off`,
      // `rate_limited` and `invalid_email` all arrive with.
      this.notice = S.forgot_sent;
    });
  }

  /**
   * One sign-in provider (R621).
   *
   * The start route sets the state cookie and redirects to the provider, so
   * this is a top-level navigation and never a fetch. The screen draws the
   * button only for a provider `GET /api/auth/methods` named, so a press
   * here always reaches a route this deployment has keys for.
   *
   * The page goes, so nothing after the assignment runs and the screen
   * needs no busy state: the browser's own progress is the answer. The
   * provider sends the browser back to `/`, and `fast/entry.ts` reads the
   * outcome off the page address.
   *
   * The press carries the deep link the screen was opened with, so the
   * callback comes back to `/?tab=clan&page=cup` and the hub opens the
   * CUP page (R1659). A password sign-in keeps the deep link without this:
   * `fast/entry.ts` holds it in memory across the screen (R1649).
   */
  private oauth(provider: string) {
    this.audio?.play('sfx-menu-press');
    // The iOS app shell runs the flow in its sign-in sheet (R2927). A
    // screen with a session asks a start ticket first, so a guest upgrades
    // in place (R2938). A failed ticket opens nothing and says so.
    // A ticket that met `reauth_required` (R2963) says so: the sign-in on
    // a stale registered session is a link.
    void startOAuth(provider, { link: this.link, session: this.me !== null }).catch((e: unknown) => e).then((ok) => {
      if (ok === true || this.closed) return;
      this.error = ok instanceof ApiError && ok.code === CODE_REAUTH_REQUIRED ? F.signin_reauth : F.signin_state;
      this.audio?.play('sfx-menu-error');
      this.render();
    });
  }

  private signOut() {
    this.audio?.play('sfx-menu-press');
    void this.guard(S.busy_signing_out, async () => {
      await authLogout();
      // The seats the tokens name belong to the account that just left.
      clearAllTokens();
      this.me = null;
      this.email = '';
      this.password = '';
    });
  }

  // --- the readouts ---

  /** The server's own instant, or the browser's while nothing has said. */
  private now(): number {
    return this.modes?.now ?? this.worlds?.now ?? Math.floor(Date.now() / 1000);
  }

  /** The entry of one mode in the answer of `/api/modes`, or undefined
   *  while nothing has said. */
  private modeOf(mode: string): ModeList['modes'][number] | undefined {
    return this.modes?.modes.find((m) => m.mode === mode);
  }

  /** How many open worlds one mode has, or -1 while nothing has said. */
  private modeWorlds(mode: string): number {
    return this.modeOf(mode)?.worlds ?? -1;
  }

  /** The population of one mode, on the same -1 rule. */
  private modePopulation(mode: string): number {
    return this.modeOf(mode)?.population ?? -1;
  }

  /**
   * The three CONQUEST readouts. The season end is the soonest one any core
   * world has reported; it is a wall-clock instant the game server already
   * converted, so the screen subtracts and never has to know which game
   * clock it came from (numbers.md §19).
   */
  private conquestReadouts(): Readout[] {
    const now = this.now();
    let soonest = 0;
    for (const w of this.worlds?.worlds ?? []) {
      if (w.seasonEndAt === undefined) continue;
      if (soonest === 0 || w.seasonEndAt < soonest) soonest = w.seasonEndAt;
    }
    const season = soonest === 0 ? DASH : formatSpan(secondsUntil(now, soonest));
    return [
      [S.read_season_ends, season],
      [S.read_empires, count(this.modePopulation('core'))],
      [S.read_day_costs, S.read_day_costs_value],
    ];
  }

  /** The three ARCADE readouts. `arcadeReadouts` above holds the rule.
   *  `modes.arcade` is what the arcade coordinators reported, absent where
   *  this deployment configures none (arcade.md §11). */
  private arcadeReadouts(): Readout[] {
    return arcadeReadouts(this.modes?.arcade, this.modeWorlds('arcade'), this.modePopulation('arcade'));
  }

  // --- rendering ---

  private render() {
    if (this.closed) return;

    // The screen re-renders when the two readout fetches land and around
    // every action, and `innerHTML` below replaces the inputs. A password
    // manager's fill does not always raise an `input` event, so what the
    // fields hold is read back here before the fields go — a fill or a
    // half-typed address must survive a readout arriving under it. The
    // focused field and its caret come back after `wire()` for the same
    // reason: a soft keyboard must not fall shut because a figure changed.
    let focused = '';
    let caret = 0;
    for (const i of this.root.querySelectorAll<HTMLInputElement>('input[data-in]')) {
      this.keep(i);
      if (document.activeElement === i) {
        focused = i.dataset.in ?? '';
        caret = i.selectionStart ?? i.value.length;
      }
    }

    const me = this.me;
    const signedIn = me !== null;
    // A mode that cannot take a join is not a join that fails; it is a
    // console that says the mode is not open yet. `arcadeIsOpen` above
    // holds the rule.
    const arcadeOpen = arcadeIsOpen(this.modes?.arcade, this.modeWorlds('arcade'));

    let html = '<div class="bar">'
      + `<div class="mark">${MARK}<div class="words"><div class="name">${S.brand}</div>`
      + `<div class="tag">${S.tagline}</div></div></div>`
      + '<div class="rt">';
    const corePop = this.modePopulation('core');
    if (corePop >= 0) {
      html += `<span>${S.commanders_at_sea({ count: esc(groupDigits(corePop)) })}</span>`;
    }
    html += '</div></div>';

    html += '<div class="stage">';

    html += this.consoleCard({
      kind: 'arcade',
      cls: 'arc',
      // The landing names a mode with the hub's own word (R2208).
      name: MODE.mode_arcade_name,
      kicker: arcadeOpen ? S.console_arcade_kicker_open : S.console_arcade_kicker_shut,
      blurb: S.console_arcade_blurb,
      readouts: this.arcadeReadouts(),
      cta: arcadeOpen ? S.cta_play_now : S.cta_opens_soon,
      act: arcadeOpen ? 'play-arcade' : '',
    });

    html += this.consoleCard({
      kind: 'conquest',
      cls: 'con',
      name: MODE.mode_core_name,
      kicker: S.console_conquest_kicker,
      blurb: S.console_conquest_blurb({ days: SEASON_DAYS }),
      readouts: this.conquestReadouts(),
      cta: signedIn ? S.cta_continue : S.cta_take_a_seat,
      act: 'play-core',
    });

    // --- the account rail ---
    html += '<div class="rail">';
    if (me !== null && me.account.kind === 'guest') {
      html += `<h3>${S.rail_played_before}</h3>`
        + `<div class="chip">${S.guest_chip({
          name: esc(me.account.name),
          honour: esc(groupDigits(me.account.honourCore)),
        })}</div>`
        + `<div class="hint">${S.hint_guest_seat}</div>`;
    } else {
      html += `<h3>${S.rail_sign_in}</h3>`;
    }
    // The provider buttons, and the divider that only means something when
    // there is something above it. A deployment with no provider keys shows
    // the email form alone, with no empty gap and no stray OR.
    const offered = PROVIDERS.filter(([p]) => this.methods?.includes(p));
    // The two buttons of the form take no press while a call is out.
    const off = this.busy === '' ? '' : ' disabled';
    if (offered.length > 0) {
      html += '<div class="oauth">'
        + offered.map(([p, cls, glyph, label]) =>
          `<button type="button" class="oa ${cls}" data-act="oauth" data-p="${p}">`
          + `<span class="g">${glyph}</span>${label}</button>`).join('')
        + '</div>'
        + `<div class="or">${S.oauth_or}</div>`;
    }
    // The hint of the reset step stands over the box it names (R2088).
    if (this.forgotHint) {
      html += `<div class="line" data-hint="forgot">${esc(S.forgot_link_hint({ button: S.btn_forgot }))}</div>`;
    }
    html +=
      // A real <form> with named fields, not loose inputs: a password
      // manager's fill and save both key on the form, the `name`s and the
      // `autocomplete` tokens, and 1Password on a telephone offered
      // nothing without them. `username` is the token the managers read on
      // a sign-in field; the field is still an e-mail address to the
      // keyboard and to us.
      '<form data-form="auth">'
      + `<input data-in="email" id="auth-email" name="email" type="email" `
      + `inputmode="email" autocomplete="username" `
      + `placeholder="${S.placeholder_email}" value="${esc(this.email)}">`
      + `<input data-in="password" id="auth-password" name="password" type="password" `
      + `autocomplete="current-password" `
      + `placeholder="${S.placeholder_password}" value="${esc(this.password)}">`
      // A quiet way out of a password that does not work, under the box
      // it belongs to (R624). `type="button"` and not a link: it stands
      // inside the form, and a submit here would sign in instead.
      + `<button type="button" class="forgot" data-act="forgot"${off}>${S.btn_forgot}</button>`
      + `<button type="submit" class="cont"${off}>`
      + `${this.busy === '' ? S.btn_continue : S.btn_busy({ label: esc(this.busy) })}</button>`
      + '</form>'
      + `<div class="hint">${S.hint_first_time}</div>`;
    if (!signedIn) {
      html += `<div class="hint">${S.hint_guest_play}</div>`;
    } else {
      html += `<div class="hint"><button class="quiet" data-act="signout">${S.btn_sign_out}</button></div>`;
    }
    // The gate stands over every other line on this rail: the two consoles
    // take no press behind it, so the screen says why and not what to do.
    if (this.gated) html += `<div class="line">${esc(S.maintenance)}</div>`;
    if (this.notice !== '') html += `<div class="line">${esc(this.notice)}</div>`;
    if (this.error !== '') html += `<div class="err">${esc(this.error)}</div>`;
    html += '</div>';

    html += '</div>';

    // --- the footer ---
    html += '<div class="foot"><div class="links">'
      + FOOT_LINKS.map(([href, label, short, legal]) =>
        `<a class="${legal ? 'legal' : ''}" href="${href}"><span class="wide">${label}</span>`
        + `<span class="narrow">${short}</span></a>`).join('')
      + '</div><div class="rule"></div>'
      + `<div class="fine">${S.fine_print}</div></div>`;

    this.root.innerHTML = html;
    this.wire();
    if (focused !== '') {
      const i = this.root.querySelector<HTMLInputElement>(`input[data-in="${focused}"]`);
      if (i) {
        i.focus();
        // Chrome throws on `setSelectionRange` for `type="email"`; there
        // the focus alone puts the caret at the end, which is where a
        // typing hand left it.
        try { i.setSelectionRange(caret, caret); } catch { /* email field */ }
      }
    }
  }

  private consoleCard(o: {
    kind: 'arcade' | 'conquest';
    cls: string;
    name: string;
    kicker: string;
    blurb: string;
    readouts: Readout[];
    cta: string;
    act: string;
  }): string {
    const shut = o.act === '';
    const press = shut ? '' : ` data-act="${o.act}"`;
    return `<div class="console ${o.cls}${shut ? ' shut' : ''}"${press}>`
      + `<div class="screen">${reelHtml(o.kind)}<div class="scrim"></div>`
      + '<div class="cut"></div>'
      + `<div class="head">${howPressHtml('how', o.kind)}<div class="kick">${esc(o.kicker)}</div>`
      + `<div class="nm">${esc(o.name)}</div>`
      + `<div class="bl">${esc(o.blurb)}</div></div></div>`
      + '<div class="deck"><div class="reads">'
      + o.readouts.map(([k, v]) =>
        `<div class="r"><div class="k">${esc(k)}</div><div class="v">${esc(v)}</div></div>`).join('')
      + '</div>'
      + `<button class="cta"${press}${shut ? ' disabled' : ''}>${esc(o.cta)}</button>`
      + '</div></div>';
  }

  private wire() {
    // The two reels. A re-render gets the videos that already play back,
    // so a readout that lands does not start a reel again.
    this.reels.attachAll(this.root);
    for (const i of this.root.querySelectorAll<HTMLInputElement>('input[data-in]')) {
      i.addEventListener('input', () => this.keep(i));
    }
    // The native submit, not a pointerdown: Enter and a soft keyboard's
    // "go" key finish the form through it, and a password manager watches
    // for it before it offers to save what was typed.
    this.root.querySelector<HTMLFormElement>('form[data-form="auth"]')
      ?.addEventListener('submit', (ev) => {
        ev.preventDefault();
        this.audio?.play('sfx-menu-press');
        this.submitForm();
      });
  }

  private act(act: string, el: HTMLElement) {
    switch (act) {
      case 'play-core': this.jumpIn('core'); break;
      case 'play-arcade': this.jumpIn('arcade'); break;
      case 'oauth': this.oauth(el.dataset.p ?? 'google'); break;
      case 'forgot': this.forgot(); break;
      case 'signout': this.signOut(); break;
      // The `?` of a console opens the HOW IT PLAYS card on its mode
      // (R2445). It stands inside the console, and the press of the
      // console reads the nearest control, so the `?` opens no seat.
      case 'how':
        this.audio?.play('sfx-menu-press');
        openHowPlays(howModeOf(el.dataset.mode), { press: () => this.audio?.play('sfx-menu-press') });
        break;
    }
  }
}
